import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Mail, MapPin, ChevronDown } from "lucide-react";
import WattZapLogo from "./WattZapLogo";
import ChatWidget from "./ChatWidget";
import WattBot from "../wattbot/WattBot";
import { openEnergyScanPortal } from "../lib/wattzapOS";
import { PRODUCT_NAV } from "../data/products";
import { COMPANY } from "../pages/Privacy";
import { initGA, trackPageView, gaEvent } from "../lib/analytics";
import { useLang, useT } from "../i18n/LanguageContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/industries", label: "Industries" },
  { to: "/journey", label: "Journey" },
  { to: "/estimator", label: "Savings Estimator" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function LangToggle({ mobile = false }) {
  const { lang, setLang } = useLang();
  const btn = (l, text) => (
    <button
      type="button"
      data-testid={`${mobile ? "mobile-" : ""}lang-${l}`}
      onClick={() => { setLang(l); gaEvent("language_changed", { language: l }); }}
      aria-pressed={lang === l}
      className={`px-2.5 py-1 text-xs transition-colors ${lang === l ? "bg-[#00D4FF] text-[#071A2D] font-semibold" : "text-white/70 hover:text-white"}`}
    >
      {text}
    </button>
  );
  return (
    <div data-testid={`${mobile ? "mobile-" : ""}lang-toggle`} className={`flex items-center rounded-full border border-white/20 overflow-hidden ${mobile ? "self-start mt-2" : ""}`}>
      {btn("en", "EN")}
      <span className="text-white/20 text-xs">|</span>
      {btn("te", "తెలుగు")}
    </div>
  );
}

function DesktopProductsMenu() {
  const t = useT();
  return (
    <div className="relative group">
      <NavLink
        to="/products"
        data-testid="nav-link-products"
        className={({ isActive }) =>
          `inline-flex items-center gap-1 text-sm transition-colors ${isActive ? "text-[#00D4FF]" : "text-white/80 hover:text-white"}`
        }
      >
        {t("Products")} <ChevronDown size={13} className="mt-0.5" />
      </NavLink>
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 hidden group-hover:block z-50" data-testid="nav-products-dropdown">
        <div className="w-60 bg-[#071A2D] border border-white/10 rounded-xl shadow-xl py-3">
          {PRODUCT_NAV.map((p) => (
            <React.Fragment key={p.to}>
              <NavLink
                to={p.to}
                end
                data-testid={`nav-dropdown-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `block px-5 py-2 text-sm transition-colors ${isActive ? "text-[#00D4FF]" : "text-white/80 hover:text-white"}`
                }
              >
                {p.label}
              </NavLink>
              {p.children && p.children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  data-testid={`nav-dropdown-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `block pl-9 pr-5 py-1.5 text-[13px] transition-colors ${isActive ? "text-[#00D4FF]" : "text-white/60 hover:text-white"}`
                  }
                >
                  {c.label}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileProductsMenu({ closeMenu }) {
  const t = useT();
  const [sub, setSub] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <NavLink
          to="/products"
          onClick={closeMenu}
          data-testid="mobile-nav-products"
          className={({ isActive }) =>
            `py-2 text-base ${isActive ? "text-[#00D4FF]" : "text-white/85"}`
          }
        >
          {t("Products")}
        </NavLink>
        <button
          onClick={() => setSub((v) => !v)}
          aria-label="Toggle products submenu"
          data-testid="mobile-nav-products-toggle"
          className="p-2 text-white/70"
        >
          <ChevronDown size={18} className={`transition-transform ${sub ? "rotate-180" : ""}`} />
        </button>
      </div>
      {sub && (
        <div className="pl-4 pb-1 flex flex-col">
          {PRODUCT_NAV.map((p) => (
            <React.Fragment key={p.to}>
              <NavLink
                to={p.to}
                end
                onClick={closeMenu}
                data-testid={`mobile-nav-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `py-1.5 text-sm ${isActive ? "text-[#00D4FF]" : "text-white/75"}`
                }
              >
                {p.label}
              </NavLink>
              {p.children && p.children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  onClick={closeMenu}
                  data-testid={`mobile-nav-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `py-1 pl-4 text-[13px] ${isActive ? "text-[#00D4FF]" : "text-white/55"}`
                  }
                >
                  {c.label}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export function Navbar() {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-[#071A2D]/95 backdrop-blur border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center" onClick={() => setOpen(false)}>
          <WattZapLogo variant="full" entrance={false} height={38} dark={true} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.slice(0, 1).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-[#00D4FF]" : "text-white/80 hover:text-white"}`
              }
              end={n.to === "/"}
            >
              {t(n.label)}
            </NavLink>
          ))}
          <DesktopProductsMenu />
          {NAV.slice(1).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-[#00D4FF]" : "text-white/80 hover:text-white"}`
              }
              end={n.to === "/"}
            >
              {t(n.label)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden lg:block"><LangToggle /></div>
          <button
            data-testid="nav-cta-audit"
            onClick={() => openEnergyScanPortal()}
            className="hidden sm:inline-flex btn-gradient px-4 py-2 rounded-full text-sm"
          >
            {t("Get a free EnergyScan")}
          </button>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#071A2D]">
          <div className="px-5 py-4 flex flex-col gap-3">
            {NAV.slice(0, 1).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `py-2 text-base ${isActive ? "text-[#00D4FF]" : "text-white/85"}`
                }
                end={n.to === "/"}
              >
                {t(n.label)}
              </NavLink>
            ))}
            <MobileProductsMenu closeMenu={() => setOpen(false)} />
            {NAV.slice(1).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `py-2 text-base ${isActive ? "text-[#00D4FF]" : "text-white/85"}`
                }
                end={n.to === "/"}
              >
                {t(n.label)}
              </NavLink>
            ))}
            <LangToggle mobile />
            <button
              onClick={() => { setOpen(false); openEnergyScanPortal(); }}
              className="btn-gradient mt-2 px-4 py-2.5 rounded-full text-sm"
              data-testid="mobile-nav-cta-audit"
            >
              {t("Get a free EnergyScan")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const t = useT();
  return (
    <footer className="bg-[#071A2D] text-white/80 mt-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <WattZapLogo variant="full" entrance={false} height={32} dark={true} />
          <p className="mt-4 text-sm text-white/70 max-w-sm">{t("Smart Energy. Lower Bills.")}</p>
          <div className="mt-6 flex items-center gap-3">
            <a
              data-testid="footer-whatsapp"
              href="https://wa.me/918639602606"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#21F59B] text-[#071A2D] font-medium px-4 py-2 rounded-full text-sm hover:brightness-105"
            >
              <MessageCircle size={16} /> {t("WhatsApp us")}
            </a>
          </div>
        </div>
        <div>
          <h4 className="text-white font-heading text-base mb-4">{t("Explore")}</h4>
          <ul className="space-y-2 text-sm">
            {[NAV[0], { to: "/products", label: "Products" }, ...NAV.slice(1)].map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-[#00D4FF]">{t(n.label)}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-heading text-base mb-4">{t("Contact")}</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5 text-[#00D4FF]" /> <span data-testid="footer-email">info@wattzap.in</span></li>
            <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 text-[#00D4FF]" /> {t("Hyderabad, Telangana, India")}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 text-xs text-white/50 space-y-2">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div>© {new Date().getFullYear()} {COMPANY.legalName} · {t("All rights reserved.")}</div>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#00D4FF] underline underline-offset-2">{t("Privacy Policy")}</Link>
              <Link to="/terms" data-testid="footer-terms" className="hover:text-[#00D4FF] underline underline-offset-2">{t("Terms of Use")}</Link>
              <span>{t("Made in Hyderabad · India")}</span>
            </div>
          </div>
          <div data-testid="footer-legal" className="text-[11px] text-white/35">
            CIN: {COMPANY.cin} · {t("Registered office:")} {COMPANY.address}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  React.useEffect(() => { initGA(); }, []);
  React.useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(location.pathname);
  }, [location.pathname]);
  return (
    <>
      <Navbar />
      <main data-testid="app-main">{children}</main>
      <Footer />
      <ChatWidget />
      <WattBot />
    </>
  );
}
